/**
 * "Today" screen — what a user sees when they sign in.
 * Fetches GET /api/home/today on mount.
 *
 * Organized by what needs the user's attention, not by content type:
 *   Today (hero) → Needs you → This week → Pickups/Polls → Recently played.
 */
import { useEffect, useState } from "react";

import {
  cancelBeacon,
  dismissSmsPrompt,
  getBeaconActivity,
  getHomeToday,
  respondToBeacon,
  rsvpSession,
  type BeaconDTO,
  type BeaconEventDTO,
  type ConfirmedPlayer,
  type HomeTodayDTO,
  type Me,
  type SessionDTO,
  type SetupState,
} from "../api";
import { PollCard } from "../components/PollCard";
import { RecentlyPlayedCard } from "../components/RecentlyPlayedCard";
import { RecordResultModal } from "../components/RecordResultModal";
import { RsvpBreakdownModal } from "../components/RsvpBreakdownModal";
import { SessionChatModal } from "../components/SessionChatModal";
import { SessionRow } from "../components/SessionRow";
import { ClubAvailability, MyWeekCard, useAvailability } from "./Availability";
import { useRecordView } from "./useRecordView";

/** "N seen" — distinct members who viewed this card (excludes the creator). */
function SeenLabel({ n }: { n: number }) {
  if (!n) return null;
  return (
    <span className="font-mono text-[10px] uppercase tracking-wider text-ink/45">
      {n} seen
    </span>
  );
}

/** Beacon "seen" pill that expands to the beacon's activity (responses + views). */
function BeaconViews({ beacon }: { beacon: BeaconDTO }) {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState<BeaconEventDTO[] | null>(null);
  useEffect(() => {
    if (!open || events !== null) return;
    getBeaconActivity(beacon.id)
      .then(setEvents)
      .catch(() => setEvents([]));
  }, [open, events, beacon.id]);
  if (beacon.view_count === 0) return null;
  return (
    <div className="mt-4 pt-3 border-t border-ink/10">
      <button
        onClick={() => setOpen((v) => !v)}
        className="cs-tap inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-ink/55 hover:text-ink"
      >
        {beacon.view_count} seen <span className="text-ink/35">{open ? "▴" : "▾"}</span>
      </button>
      {open && <BeaconActivityList events={events} />}
    </div>
  );
}

function BeaconActivityList({ events }: { events: BeaconEventDTO[] | null }) {
  if (events === null) return <div className="text-xs text-ink/45 mt-2">Loading…</div>;
  if (events.length === 0) return <div className="text-xs text-ink/45 mt-2">No activity yet.</div>;
  return (
    <ul className="mt-2 space-y-1.5">
      {events.map((e) => {
        const time = new Date(e.created_at).toLocaleString(undefined, {
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        });
        const label =
          e.kind === "response"
            ? e.response === "accepted"
              ? "is in"
              : "passed"
            : `viewed${e.source === "email" ? " (email)" : ""}`;
        const dot =
          e.kind === "response"
            ? e.response === "accepted"
              ? "bg-success"
              : "bg-[#C84B31]"
            : "bg-ink/25";
        return (
          <li key={e.id} className="flex items-center gap-2 text-xs">
            <span className={"w-1.5 h-1.5 rounded-full shrink-0 " + dot} />
            <span className="truncate">
              <b className="font-display">{e.is_you ? "You" : e.name}</b>{" "}
              <span className="text-ink/55">{label}</span>
            </span>
            <span className="ml-auto font-mono text-[10px] text-ink/45 shrink-0">{time}</span>
          </li>
        );
      })}
    </ul>
  );
}

export function Home({
  me,
  onAskAgent,
  onOpenSms,
  setupHidden,
  onDismissSetup,
  availHidden,
  onDismissAvail,
  onReopenAvail,
}: {
  me: Me;
  onAskAgent: (prefill?: string) => void;
  onOpenSms: () => void;
  setupHidden: boolean;
  onDismissSetup: () => void;
  availHidden: boolean;
  onDismissAvail: () => void;
  onReopenAvail: () => void;
}) {
  const firstName = (me.name ?? "").split(" ")[0] || "there";
  const [smsBannerHidden, setSmsBannerHidden] = useState(false);
  const [data, setData] = useState<HomeTodayDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rsvpModalSession, setRsvpModalSession] = useState<SessionDTO | null>(null);
  const [chatTarget, setChatTarget] = useState<{ id: string; title: string } | null>(null);
  const [recordTarget, setRecordTarget] = useState<{
    sessionId: string;
    groupId: string;
    format: "singles" | "doubles";
  } | null>(null);
  // Shared by the top-of-page MyWeekCard and the lower ClubAvailability grids —
  // must be called before the early returns below (rules of hooks). Skips the
  // fetch entirely while the user has the feature ✕-ed off.
  const avail = useAvailability(!availHidden);

  useEffect(() => {
    let cancelled = false;
    getHomeToday()
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError(String(e?.message ?? e)));
    return () => { cancelled = true; };
  }, []);

  async function handleRsvp(sessionId: string, intent: "yes" | "no") {
    try {
      await rsvpSession(sessionId, intent);
    } catch (e) {
      setError(String((e as Error).message));
      return;
    }
    // Refresh — server is now the source of truth.
    const fresh = await getHomeToday();
    setData(fresh);
  }

  async function handleBeaconResponse(beaconId: string, intent: "accept" | "decline") {
    try {
      await respondToBeacon(beaconId, intent);
    } catch (e) {
      setError(String((e as Error).message));
      return;
    }
    const fresh = await getHomeToday();
    setData(fresh);
  }

  async function handleCancelBeacon(beaconId: string) {
    try {
      await cancelBeacon(beaconId);
    } catch (e) {
      setError(String((e as Error).message));
      return;
    }
    const fresh = await getHomeToday();
    setData(fresh);
  }

  // --- States --------------------------------------------------------------
  if (error) {
    return (
      <div className="max-w-5xl mx-auto px-5 pt-12">
        <div className="border-2 border-ink rounded-md p-6">
          <div className="font-display font-bold text-xl mb-2">Couldn't load your week.</div>
          <div className="text-ink/65 text-sm mb-3">{error}</div>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-ink text-bg font-display font-bold text-sm cs-tap"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
  if (!data) return <HomeSkeleton />;

  // --- Buckets: by what needs the user, not by content type ----------------
  const sessions = data.sessions; // already sorted by starts_at ASC
  const todaySessions = sessions.filter((s) => isToday(s.starts_at));
  const laterSessions = sessions.filter((s) => !isToday(s.starts_at));
  const needsRsvp = laterSessions.filter((s) => s.my_status == null);
  const thisWeek = laterSessions.filter((s) => s.my_status != null);

  const polls = data.polls ?? [];
  const unvotedPolls = polls.filter((p) => !p.i_voted);
  const votedPolls = polls.filter((p) => p.i_voted);
  // Beacons you sent (outgoing) vs. ones you were pinged for (incoming).
  const myBeacons = data.beacons.filter((b) => b.is_mine);
  const pendingBeacons = data.beacons.filter((b) => !b.is_mine && b.my_response == null);
  const answeredBeacons = data.beacons.filter((b) => !b.is_mine && b.my_response != null);
  // Open pickups (incoming you can join + your own) — the most time-sensitive
  // thing on the page (≈4h window), surfaced in one section up top.
  const openBeacons = pendingBeacons.length + myBeacons.length;

  const recent = data.recently_played ?? [];
  // Games you played that still need a score = an action; the rest are info.
  const needsScore = recent.filter((s) => s.my_status === "confirmed" && !s.has_result);
  const restRecent = recent.filter((s) => !(s.my_status === "confirmed" && !s.has_result));

  // Beacons live in their own top section now, so they're not double-counted here.
  const needsYouCount =
    needsRsvp.length + unvotedPolls.length + needsScore.length;
  const isEmpty =
    todaySessions.length === 0 &&
    needsYouCount === 0 &&
    thisWeek.length === 0 &&
    votedPolls.length === 0 &&
    answeredBeacons.length === 0 &&
    myBeacons.length === 0 &&
    restRecent.length === 0;

  // No games on the books (today, this week, or awaiting your RSVP). When the
  // page still has other content (history, polls) `isEmpty` is false, so the
  // full EmptyState never fires — this is the gap the nudge fills.
  const noUpcoming =
    todaySessions.length === 0 && thisWeek.length === 0 && needsRsvp.length === 0;
  const showNudge = noUpcoming && !isEmpty && data.has_clubs;
  // The thin "Ideas" strip is the steady-state nudge when you DO have games;
  // when you don't, the richer UpcomingNudge replaces it (no duplicate CTAs).
  const showIdeas = setupHidden && data.has_clubs && !showNudge;

  function recordScore(s: SessionDTO) {
    if (s.format) setRecordTarget({ sessionId: s.id, groupId: s.group_id, format: s.format });
  }

  return (
    <div className="max-w-5xl mx-auto px-5 pt-8">
      <div className="cs-reveal mb-8">
        <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/55 mb-1">
          {new Date().toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
        </div>
        <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight leading-[0.95]">
          Hey {firstName}.
        </h1>
        <p className="text-ink/65 mt-2 text-lg">
          {summarize(data, needsYouCount, todaySessions)}
        </p>
      </div>

      {/* Your week — the user's own availability grid, always at the top so
          it stays visible and gets kept accurate. Club aggregates live lower.
          The ✕ hides the whole feature; "My availability" in the profile menu
          brings it back. */}
      {data.has_clubs && !availHidden && (
        <MyWeekCard
          avail={avail}
          firstName={me.name ? firstName : null}
          onDismiss={onDismissAvail}
        />
      )}

      {/* Getting-started checklist — auto-checks from real activity. */}
      {!setupHidden && (
        <GettingStarted
          setup={data.setup}
          onAskAgent={onAskAgent}
          onOpenSms={onOpenSms}
          onDismiss={onDismissSetup}
          onReopenAvail={onReopenAvail}
        />
      )}

      {/* One-time SMS opt-in nudge — only when the checklist (which has its own
          SMS task) isn't showing, so we don't double-nudge. */}
      {setupHidden && data.show_sms_prompt && !smsBannerHidden && (
        <div className="cs-reveal mb-6 border-2 border-ink rounded-md p-4 flex items-center gap-3 bg-bg">
          <div className="text-2xl shrink-0" aria-hidden>📱</div>
          <div className="min-w-0 flex-1">
            <div className="font-display font-bold text-sm">Get game updates by text</div>
            <div className="text-ink/60 text-xs">
              Reminders, RSVPs, and pickup invites — straight to your phone.
            </div>
          </div>
          <button
            onClick={() => {
              setSmsBannerHidden(true);
              onOpenSms();
            }}
            className="cs-tap shrink-0 px-3 py-1.5 bg-accent text-ink text-[11px] uppercase tracking-wider font-bold rounded-sm"
          >
            Turn on
          </button>
          <button
            onClick={() => {
              setSmsBannerHidden(true);
              void dismissSmsPrompt();
            }}
            aria-label="Dismiss"
            className="cs-tap shrink-0 text-ink/40 hover:text-ink font-mono text-lg leading-none"
          >
            ✕
          </button>
        </div>
      )}

      {/* Always-on quick ideas — discoverable actions even when you have games. */}
      {showIdeas && <IdeasStrip isAdmin={data.is_admin_anywhere} onAskAgent={onAskAgent} />}

      {/* Nothing booked, but you have history — nudge the next game, history-aware. */}
      {showNudge && (
        <UpcomingNudge
          recentlyPlayed={recent}
          isAdmin={data.is_admin_anywhere}
          onAskAgent={onAskAgent}
        />
      )}

      {/* Pickups — open beacons (incoming you can join + your own). Surfaced up
          top because they're the most time-sensitive item (≈4h window) and were
          getting buried below sessions, polls, and the availability grid. */}
      {openBeacons > 0 && (
        <div className="mb-6">
          <SectionLabel>Pickups · {openBeacons}</SectionLabel>
          <div className="grid sm:grid-cols-2 gap-4">
            {pendingBeacons.map((b) => (
              <BeaconCard key={b.id} beacon={b} onRespond={handleBeaconResponse} />
            ))}
            {myBeacons.map((b) => (
              <MyBeaconCard key={b.id} beacon={b} onCancel={handleCancelBeacon} />
            ))}
          </div>
        </div>
      )}

      {/* Today — sessions happening today, hero treatment. */}
      {todaySessions.length > 0 && (
        <div className="mb-6">
          <SectionLabel>Today</SectionLabel>
          <div className="space-y-4">
            {todaySessions.map((s, i) => (
              <HeroSession
                key={s.id}
                session={s}
                onRsvp={handleRsvp}
                onShowRsvps={() => setRsvpModalSession(s)}
                onOpenChat={() => setChatTarget({ id: s.id, title: s.starts_at_display })}
                emphasis={i === 0 ? "primary" : "secondary"}
              />
            ))}
          </div>
        </div>
      )}

      {/* This week — upcoming sessions you've already responded to. Kept above
          "Needs you" so an existing game this week always leads. */}
      {thisWeek.length > 0 && (
        <div className="mb-6">
          <SectionLabel>This week</SectionLabel>
          <div className="space-y-2">
            {thisWeek.map((s) => (
              <SessionRow
                key={s.id}
                session={s}
                isAdmin={false}
                onRsvp={handleRsvp}
                onShowRsvps={() => setRsvpModalSession(s)}
                onOpenChat={() => setChatTarget({ id: s.id, title: s.starts_at_display })}
                onAssignCourts={() => undefined}
                onRecord={() => recordScore(s)}
                onShowResults={() => undefined}
                resultsVisible={false}
              />
            ))}
          </div>
        </div>
      )}

      {/* Needs you — everything awaiting your response, not happening today. */}
      {needsYouCount > 0 && (
        <div className="mb-6">
          <SectionLabel>Needs you · {needsYouCount}</SectionLabel>
          <div className="space-y-3">
            {needsRsvp.map((s) => (
              <SessionRow
                key={s.id}
                session={s}
                isAdmin={false}
                onRsvp={handleRsvp}
                onShowRsvps={() => setRsvpModalSession(s)}
                onOpenChat={() => setChatTarget({ id: s.id, title: s.starts_at_display })}
                onAssignCourts={() => undefined}
                onRecord={() => recordScore(s)}
                onShowResults={() => undefined}
                resultsVisible={false}
              />
            ))}
            {unvotedPolls.map((p) => (
              <PollCard key={p.id} poll={p} />
            ))}
            {needsScore.map((s) => (
              <RecentlyPlayedCard
                key={s.id}
                session={s}
                isAdmin={false}
                resultsVisible={false}
                onRecord={() => recordScore(s)}
                onShowResults={() => undefined}
              />
            ))}
          </div>
        </div>
      )}

      {/* Club availability — each club's aggregated grid (who can play when). */}
      {data.has_clubs && !availHidden && (
        <ClubAvailability avail={avail} onAskAgent={onAskAgent} />
      )}

      {/* Pickups you've answered + polls you've voted — kept visible. */}
      {answeredBeacons.length > 0 && (
        <div className="mb-6">
          <SectionLabel>Pickups</SectionLabel>
          <div className="grid sm:grid-cols-2 gap-4">
            {answeredBeacons.map((b) => (
              <BeaconCard key={b.id} beacon={b} onRespond={handleBeaconResponse} />
            ))}
          </div>
        </div>
      )}

      {votedPolls.length > 0 && (
        <div className="mb-6">
          <SectionLabel>Polls</SectionLabel>
          <div className="space-y-4">
            {votedPolls.map((p) => (
              <PollCard key={p.id} poll={p} />
            ))}
          </div>
        </div>
      )}

      {restRecent.length > 0 && (
        <div className="mb-6">
          <SectionLabel>Recently played</SectionLabel>
          <div className="space-y-2">
            {restRecent.map((s) => (
              <RecentlyPlayedCard
                key={s.id}
                session={s}
                isAdmin={false}
                resultsVisible={false}
                onRecord={() => recordScore(s)}
                onShowResults={() => undefined}
              />
            ))}
          </div>
        </div>
      )}

      {isEmpty && (
        <EmptyState
          hasClubs={data.has_clubs}
          isAdmin={data.is_admin_anywhere}
          onAskAgent={onAskAgent}
        />
      )}

      <div className="mt-12 mb-8 text-center font-mono text-[11px] uppercase tracking-[0.18em] text-ink/45">
        Need something specific? Tap the yellow bar below ↓
      </div>

      {rsvpModalSession && (
        <RsvpBreakdownModal
          session={rsvpModalSession}
          onClose={() => setRsvpModalSession(null)}
        />
      )}
      {chatTarget && (
        <SessionChatModal
          sessionId={chatTarget.id}
          title={chatTarget.title}
          meId={me.uid}
          onClose={() => setChatTarget(null)}
        />
      )}
      {recordTarget && (
        <RecordResultModal
          groupId={recordTarget.groupId}
          sessionId={recordTarget.sessionId}
          defaultFormat={recordTarget.format}
          onClose={async (recorded) => {
            setRecordTarget(null);
            if (recorded) setData(await getHomeToday());
          }}
        />
      )}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink/55 mb-3">
      {children}
    </div>
  );
}

// Getting-started checklist — each row auto-checks from real activity (server
// `setup` flags); the actions seed the chat or open the SMS opt-in.
function GettingStarted({
  setup,
  onAskAgent,
  onOpenSms,
  onDismiss,
  onReopenAvail,
}: {
  setup: SetupState;
  onAskAgent: (prefill?: string) => void;
  onOpenSms: () => void;
  onDismiss: () => void;
  onReopenAvail: () => void;
}) {
  const tasks: {
    key: string;
    label: string;
    desc: string;
    cta: string;
    done: boolean;
    action: (() => void) | null;
  }[] = [
    {
      key: "profile",
      label: "Set up your profile",
      desc: "Your name, sport, and skill so games and matchmaking fit you.",
      cta: "",
      done: setup.profile,
      action: null,
    },
    {
      key: "club",
      label: "Start or join a club",
      desc: "Your group's home base — games, RSVPs, and standings all live in a club.",
      cta: "Set up",
      done: setup.club,
      // The label offers both paths, so don't presume "start": let the agent ask
      // whether they have an invite code or want to create a club from scratch.
      action: () =>
        onAskAgent(
          "I want to get into a club but I'm not sure whether to start a new one or join an existing one — can you help me figure out which?",
        ),
    },
    {
      key: "availability",
      label: "Share your weekly availability",
      desc: "Tap when you can usually play so clubmates can find you for games — only your club sees it.",
      cta: "Set",
      done: setup.availability,
      // The grid lives at the top of this very page — un-hide it if the user
      // ✕-ed the feature off, then bring it into view.
      action: () => {
        onReopenAvail();
        window.scrollTo({ top: 0, behavior: "smooth" });
      },
    },
    {
      key: "rsvp",
      label: "RSVP to a game",
      desc: "Say you're in on a game so the club knows you're playing — and you get a court.",
      cta: "View",
      done: setup.rsvp,
      action: () => onAskAgent("What games can I RSVP to?"),
    },
    ...(setup.is_admin
      ? [
          {
            key: "weekly",
            label: "Set up a weekly game",
            desc: "A recurring slot like Tuesday 7pm — ClubBot invites everyone every week.",
            cta: "Set up",
            done: setup.weekly,
            action: () => onAskAgent("Set up a weekly recurring game for my club"),
          },
        ]
      : []),
    {
      key: "beacon",
      label: "Find a player for a pickup",
      desc: "Short a player? Ping skill-matched members to fill a last-minute game.",
      cta: "Find",
      done: setup.beacon,
      action: () => onAskAgent("Find me a player for a pickup game"),
    },
    {
      key: "result",
      label: "Record a match score",
      desc: "Log who won — everyone climbs the singles & doubles ladders from real results.",
      cta: "Record",
      done: setup.result,
      action: () => onAskAgent("Record a match result"),
    },
    {
      key: "sms",
      label: "Turn on text updates",
      desc: "Reminders, RSVPs, and pickup invites by text — and reply to RSVP.",
      cta: "Turn on",
      done: setup.sms,
      action: onOpenSms,
    },
  ];
  const done = tasks.filter((t) => t.done).length;
  const total = tasks.length;
  const allDone = done === total;
  const pct = Math.round((done / total) * 100);

  return (
    <div className="cs-reveal mb-6 border-2 border-ink rounded-md overflow-hidden">
      <div className="px-5 py-4 bg-ink text-bg flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-bg/60">
            Getting started
          </div>
          <div className="font-display font-bold text-lg leading-tight">
            {allDone ? "You're all set! 🎉" : `${done} of ${total} done`}
          </div>
        </div>
        <button
          onClick={onDismiss}
          aria-label="Dismiss"
          className="shrink-0 text-bg/50 hover:text-bg cs-tap font-mono text-lg leading-none"
        >
          ✕
        </button>
      </div>
      <div className="h-1.5 bg-ink/10">
        <div
          className="h-full bg-accent transition-[width] duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      {allDone ? (
        <div className="px-5 py-6 text-center bg-bg">
          <div className="text-ink/65 text-sm mb-3">
            Nice — you've got the hang of ClubBot.
          </div>
          <button
            onClick={onDismiss}
            className="cs-tap px-5 py-2 bg-ink text-bg font-display font-bold text-sm rounded-md"
          >
            Done
          </button>
        </div>
      ) : (
        <ul className="divide-y divide-ink/10 bg-bg">
          {tasks.map((t) => (
            <li key={t.key} className="flex items-start gap-3 px-5 py-3">
              <span
                className={
                  "mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 text-[11px] font-bold " +
                  (t.done ? "bg-success text-bg" : "border-2 border-ink/25 text-transparent")
                }
              >
                ✓
              </span>
              <div className="flex-1 min-w-0">
                <div
                  className={
                    "text-sm " +
                    (t.done ? "text-ink/40 line-through" : "font-display font-bold")
                  }
                >
                  {t.label}
                </div>
                {!t.done && (
                  <div className="text-ink/55 text-xs leading-snug mt-0.5">{t.desc}</div>
                )}
              </div>
              {!t.done && t.action && (
                <button
                  onClick={t.action}
                  className="cs-tap shrink-0 mt-0.5 px-3 py-1.5 bg-accent text-ink text-[11px] uppercase tracking-wider font-bold rounded-sm"
                >
                  {t.cta} →
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function summarize(d: HomeTodayDTO, needsYouCount: number, today: SessionDTO[]): string {
  const parts: string[] = [];
  // Open pickups = incoming (unanswered) + your own — the top "Pickups" section.
  const pickups = d.beacons.filter((b) => b.is_mine || b.my_response == null).length;
  if (pickups > 0) {
    parts.push(`${pickups} pickup${pickups === 1 ? "" : "s"} open`);
  }
  if (today.length > 0) {
    parts.push(`next game ${friendlyDelta(minutesUntil(today[0]!.starts_at))}`);
  }
  if (needsYouCount > 0) {
    parts.push(`${needsYouCount} thing${needsYouCount === 1 ? "" : "s"} need${needsYouCount === 1 ? "s" : ""} you`);
  }
  if (parts.length) return capitalize(parts.join(" · ")) + ".";
  const clubs = new Set(d.sessions.map((s) => s.group_id)).size;
  if (clubs) return `${clubs} club${clubs === 1 ? "" : "s"} active this week — nothing needs you right now.`;
  return "Nothing on the books this week.";
}

function capitalize(s: string): string {
  return s.length ? s[0]!.toUpperCase() + s.slice(1) : s;
}

function isToday(iso: string): boolean {
  const d = new Date(iso);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

function minutesUntil(iso: string): number {
  return Math.max(0, Math.floor((new Date(iso).getTime() - Date.now()) / 60000));
}

// Initials for an avatar from a display name (beacon creator is a first name).
function nameInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
  return (parts[0] ?? "?").slice(0, 2).toUpperCase();
}

// "Doubles · 2 courts" — courts omitted when unset.
function formatLabel(s: SessionDTO): string {
  const fmt = s.format === "singles" ? "Singles" : "Doubles";
  if (s.court_count == null) return fmt;
  return `${fmt} · ${s.court_count} court${s.court_count === 1 ? "" : "s"}`;
}

// Overlapping initials circles — "who's in" social proof. `total` is the full
// confirmed count so we can show a "+N" when it exceeds the avatars we have.
function Avatars({ players, total }: { players: ConfirmedPlayer[]; total: number }) {
  if (players.length === 0) return null;
  const extra = total - players.length;
  return (
    <div className="flex items-center">
      {players.map((p, i) => (
        <div
          key={p.user_id}
          title={p.name}
          className={
            "w-6 h-6 rounded-full border-2 border-bg flex items-center justify-center font-display font-bold text-[9px] shrink-0 " +
            (i > 0 ? "-ml-1.5 " : "") +
            (p.is_you ? "bg-accent text-ink" : "bg-ink text-bg")
          }
        >
          {p.initials}
        </div>
      ))}
      {extra > 0 && (
        <div className="-ml-1.5 w-6 h-6 rounded-full border-2 border-bg bg-bg-3 text-ink flex items-center justify-center font-mono text-[9px] shrink-0">
          +{extra}
        </div>
      )}
    </div>
  );
}

// Compact session row for the "Needs you" + "This week" lists. Shows quick RSVP
// when you haven't responded, else your status + a countdown.
function HomeSkeleton() {
  return (
    <div className="max-w-5xl mx-auto px-5 pt-8 cs-reveal">
      <div className="h-8 w-32 bg-ink/10 rounded mb-3" />
      <div className="h-12 w-60 bg-ink/10 rounded mb-2" />
      <div className="h-5 w-80 bg-ink/10 rounded mb-8" />
      <div className="h-56 w-full bg-ink/10 rounded-md mb-5" />
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="h-32 bg-ink/10 rounded-md" />
        <div className="h-32 bg-ink/10 rounded-md" />
      </div>
    </div>
  );
}

function EmptyState({
  hasClubs,
  isAdmin,
  onAskAgent,
}: {
  hasClubs: boolean;
  isAdmin: boolean;
  onAskAgent: (prefill?: string) => void;
}) {
  // A brand-new solo sign-up belongs to no club yet — the launchpad pitches what
  // ClubBot does (as outcomes) and drives the one thing to do: start a club.
  if (!hasClubs) {
    const features = [
      { icon: "📅", text: "Set up games — ClubBot asks everyone if they're in" },
      { icon: "🎾", text: "Short a player? Ping the group to fill the spot fast" },
      { icon: "🤖", text: "Reminders, no-shows, and court booking — all handled" },
    ];
    return (
      <div className="border-2 border-ink rounded-md p-8 cs-reveal">
        <div className="text-center">
          <div className="font-display font-bold text-2xl sm:text-3xl mb-2">
            Run your crew — without the group chat.
          </div>
          <div className="text-ink/65 mb-6 max-w-md mx-auto">
            ClubBot schedules your games, chases the RSVPs, and books the courts —
            so you just show up and play.
          </div>
        </div>

        <ul className="max-w-sm mx-auto mb-7 space-y-2.5">
          {features.map((f) => (
            <li key={f.text} className="flex items-center gap-3 text-sm text-ink/80">
              <span className="text-lg shrink-0">{f.icon}</span>
              <span>{f.text}</span>
            </li>
          ))}
        </ul>

        <div className="flex flex-wrap gap-2 justify-center">
          <button
            onClick={() => onAskAgent("Help me start a new club")}
            className="px-5 py-2.5 bg-accent text-ink font-display font-bold text-sm cs-tap"
          >
            Create your club
          </button>
          <button
            onClick={() => onAskAgent("I have an invite code to join a club")}
            className="px-5 py-2.5 border-2 border-ink/20 font-display font-bold text-sm cs-tap"
          >
            Join with a code
          </button>
        </div>
        <div className="mt-4 text-center font-mono text-[11px] tracking-wide text-ink/45">
          2 minutes · you'll be the admin · or just tell ClubBot “set up my Tuesday tennis group”
        </div>
      </div>
    );
  }
  return (
    <div className="border-2 border-ink rounded-md p-8 text-center cs-reveal">
      <div className="font-display font-bold text-2xl mb-2">All quiet.</div>
      <div className="text-ink/65 mb-5">
        {isAdmin
          ? "No games on the calendar this week — want to set one up?"
          : "Nothing scheduled in your clubs. Want to find a pickup game?"}
      </div>
      <div className="flex flex-wrap gap-2 justify-center">
        <button
          onClick={() =>
            onAskAgent(
              isAdmin
                ? "Schedule a game this week and ask everyone to RSVP"
                : "Find me a pickup game this weekend",
            )
          }
          className="px-4 py-2 bg-ink text-bg font-display font-bold text-sm cs-tap"
        >
          {isAdmin ? "Schedule a game" : "Find a pickup"}
        </button>
        <button
          onClick={() =>
            onAskAgent(isAdmin ? "Open a pickup beacon for this weekend" : "What can you help me with?")
          }
          className="px-4 py-2 border-2 border-ink/20 font-display font-bold text-sm cs-tap"
        >
          {isAdmin ? "Fire a pickup beacon" : "Ask ClubBot"}
        </button>
      </div>
    </div>
  );
}

// Thin, always-on row of tappable ideas. Role-aware; each chip seeds the chat.
function IdeasStrip({
  isAdmin,
  onAskAgent,
}: {
  isAdmin: boolean;
  onAskAgent: (prefill?: string) => void;
}) {
  const ideas = isAdmin
    ? [
        { label: "Schedule a game", prefill: "Schedule a game this week and ask everyone to RSVP" },
        { label: "Weekly slot", prefill: "Set up a weekly recurring game for my club" },
        { label: "Find a player", prefill: "Find me a player for a pickup game" },
      ]
    : [
        { label: "Find a player", prefill: "Find me a player for a pickup game" },
        { label: "Record a score", prefill: "Record a match result" },
        { label: "Ask ClubBot", prefill: "" },
      ];
  return (
    <div className="cs-reveal mb-6 flex items-center gap-2 flex-wrap">
      <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink/45 mr-1">
        Ideas
      </span>
      {ideas.map((i) => (
        <button
          key={i.label}
          onClick={() => onAskAgent(i.prefill)}
          className="cs-tap px-3 py-1.5 border border-ink/20 rounded-full text-xs font-display font-bold hover:border-ink hover:bg-ink hover:text-bg transition-colors"
        >
          + {i.label}
        </button>
      ))}
    </div>
  );
}

// Shown when nothing's booked but the user has recent history. Leads with a
// "run it back" CTA built from the most recent past session, then role fallbacks.
function UpcomingNudge({
  recentlyPlayed,
  isAdmin,
  onAskAgent,
}: {
  recentlyPlayed: SessionDTO[];
  isAdmin: boolean;
  onAskAgent: (prefill?: string) => void;
}) {
  // Most recent past session powers the personalized "play again" suggestion.
  const last = recentlyPlayed
    .slice()
    .sort((a, b) => (a.starts_at < b.starts_at ? 1 : -1))[0];

  const ctas: { label: string; prefill: string; primary: boolean }[] = [];
  if (last) {
    const slot = slotLabel(last.starts_at);
    ctas.push(
      isAdmin
        ? {
            label: "Play again →",
            prefill: `Schedule another ${last.sport} game with ${last.group_name} for ${slot} next week and ask everyone to RSVP`,
            primary: true,
          }
        : {
            label: "Round up the crew →",
            prefill: `Find players for a pickup ${slot} like my last game with ${last.group_name}`,
            primary: true,
          },
    );
  }
  if (isAdmin) {
    if (!last)
      ctas.push({ label: "Schedule a game", prefill: "Schedule a game this week and ask everyone to RSVP", primary: true });
    ctas.push({ label: "Set up a weekly slot", prefill: "Set up a weekly recurring game for my club", primary: false });
  } else {
    ctas.push({ label: "Find a player", prefill: "Find me a player for a pickup game", primary: !last });
  }

  return (
    <div className="cs-reveal mb-6 border-2 border-ink rounded-md p-6">
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink/45 mb-2">
        Nothing on the calendar
      </div>
      <div className="font-display font-bold text-2xl mb-1">Line up your next game.</div>
      <div className="text-ink/65 text-sm mb-5">
        {last ? (
          <>
            You last played{" "}
            <span className="font-bold text-ink">{slotLabel(last.starts_at)}</span> with{" "}
            {last.group_name}. Run it back?
          </>
        ) : isAdmin ? (
          "No games booked — set one up and the whole club gets invited."
        ) : (
          "No games booked — find a pickup or ping a player to fill a court."
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {ctas.map((c) => (
          <button
            key={c.label}
            onClick={() => onAskAgent(c.prefill)}
            className={
              "cs-tap px-4 py-2 font-display font-bold text-sm rounded-sm " +
              (c.primary ? "bg-accent text-ink" : "border-2 border-ink/20")
            }
          >
            {c.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// "Tuesday 7:00 PM" — weekday + time from an ISO timestamp, for re-book nudges.
function slotLabel(iso: string): string {
  const d = new Date(iso);
  return (
    d.toLocaleDateString(undefined, { weekday: "long" }) +
    " " +
    d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
  );
}

function HeroSession({
  session,
  onRsvp,
  onShowRsvps,
  onOpenChat,
  emphasis = "primary",
}: {
  session: SessionDTO;
  onRsvp: (sessionId: string, intent: "yes" | "no") => void;
  onShowRsvps: () => void;
  onOpenChat: () => void;
  /** "primary" = the most-imminent today session, gets the big hero treatment.
   *  "secondary" = additional today sessions, more compact. */
  emphasis?: "primary" | "secondary";
}) {
  const ref = useRecordView<HTMLDivElement>("session", session.id);
  const inText = friendlyDelta(minutesUntil(session.starts_at));

  const banner =
    session.my_status === "confirmed"
      ? (session.my_court != null
          ? `You're on Court ${session.my_court_label ?? session.my_court} · `
          : "You're in · ") + inText
    : session.my_status === "waitlist" ? "On the waitlist · " + inText
    : session.my_status === "no" ? "You're out · " + inText
    : "Up next · " + inText;

  const isPrimary = emphasis === "primary";
  const containerCls = isPrimary
    ? "cs-reveal cs-stripes border-2 border-ink rounded-md overflow-hidden"
    : "cs-reveal border-2 border-ink/15 rounded-md overflow-hidden bg-bg";
  const headerCls = isPrimary
    ? "px-6 sm:px-8 py-5 flex items-center justify-between bg-ink text-bg"
    : "px-5 sm:px-6 py-3 flex items-center justify-between bg-bg-2 text-ink border-b border-ink/10";
  const bodyCls = isPrimary
    ? "px-6 sm:px-8 py-6 grid sm:grid-cols-[1fr,auto] gap-6 items-end"
    : "px-5 sm:px-6 py-4 grid sm:grid-cols-[1fr,auto] gap-4 items-end";
  const displayCls = isPrimary
    ? "font-display font-bold text-3xl sm:text-4xl leading-[0.95] tracking-tight"
    : "font-display font-bold text-xl sm:text-2xl leading-tight tracking-tight";
  const countCls = isPrimary
    ? "font-display font-bold text-6xl leading-none num-tab"
    : "font-display font-bold text-3xl leading-none num-tab";
  const countSubCls = isPrimary ? "text-ink/25 text-4xl" : "text-ink/25 text-xl";
  const footerCls = isPrimary
    ? "px-6 sm:px-8 pb-6 flex flex-wrap gap-2"
    : "px-5 sm:px-6 pb-4 flex flex-wrap gap-2";

  return (
    <div ref={ref} className={containerCls}>
      <div className={headerCls}>
        <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] min-w-0">
          <span
            className={
              "w-2 h-2 rounded-full shrink-0 " +
              (session.my_status === "confirmed"
                ? "bg-success"
                : session.my_status === "no"
                  ? (isPrimary ? "bg-ink/40" : "bg-ink/30")
                  : "bg-accent animate-pulse")
            }
          />
          <span className="truncate">{banner}</span>
        </div>
        <div className={
          "font-mono text-[11px] uppercase tracking-[0.14em] shrink-0 ml-2 " +
          (isPrimary ? "text-bg/60" : "text-ink/55")
        }>
          {session.group_name}
        </div>
      </div>
      <div className={bodyCls}>
        <div className="min-w-0">
          <div className={displayCls}>{session.starts_at_display}</div>
          <div className={"mt-2 " + (isPrimary ? "text-ink/65" : "text-ink/55 text-sm")}>
            {session.location ?? "Location TBD"}
          </div>
          {session.format && (
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/55 mt-1">
              {formatLabel(session)}
            </div>
          )}
          {session.my_court != null && (
            <div className="mt-2 inline-flex items-center gap-1 bg-accent text-ink font-display font-bold text-sm px-2.5 py-1 rounded-sm">
              🎾 Court {session.my_court_label ?? session.my_court}
            </div>
          )}
          {session.confirmed_players.length > 0 && (
            <button onClick={onShowRsvps} className="mt-3 flex items-center gap-2 cs-tap">
              <Avatars players={session.confirmed_players} total={session.confirmed} />
              <span className="font-mono text-[10px] uppercase tracking-wider text-ink/55">
                in →
              </span>
            </button>
          )}
        </div>
        <div className="text-right">
          <div className={countCls}>
            {session.confirmed}
            {session.capacity != null && (
              <span className={countSubCls}>/{session.capacity}</span>
            )}
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/55 mt-1">
            {session.capacity != null ? "confirmed" : "confirmed · no cap"}
          </div>
          {isPrimary && <RsvpBreakdown session={session} />}
        </div>
      </div>
      <div className={footerCls + " items-center"}>
        <RsvpButtons session={session} onRsvp={onRsvp} compact={!isPrimary} />
        <button
          onClick={onOpenChat}
          className={
            "cs-tap border-2 border-ink/15 hover:border-ink/40 font-display font-bold transition " +
            (isPrimary ? "px-6 py-3 text-base" : "px-4 py-2 text-sm")
          }
        >
          💬 Chat
        </button>
        <div className="ml-auto flex items-center gap-3">
          <SeenLabel n={session.view_count} />
          <button
            onClick={onShowRsvps}
            className="font-mono text-[10px] uppercase tracking-wider text-ink/55 hover:text-ink underline decoration-accent-dim decoration-2 underline-offset-2"
          >
            See who →
          </button>
        </div>
      </div>
    </div>
  );
}

function RsvpBreakdown({ session }: { session: SessionDTO }) {
  // Tiny mono row showing the yes/no/waitlist/no-reply split.
  // Only renders chips for buckets > 0 so an empty session doesn't show "0 in · 0 out · 0 no-reply".
  const parts: { n: number; label: string; cls: string }[] = [];
  if (session.confirmed > 0) parts.push({ n: session.confirmed, label: "in", cls: "text-success" });
  if (session.waitlist > 0) parts.push({ n: session.waitlist, label: "wait", cls: "text-ink/60" });
  if (session.declined > 0) parts.push({ n: session.declined, label: "out", cls: "text-[#C84B31]" });
  if (session.no_reply > 0) parts.push({ n: session.no_reply, label: "not asked", cls: "text-ink/45" });
  if (!parts.length) return null;
  return (
    <div className="font-mono text-[10px] uppercase tracking-[0.14em] mt-2 flex flex-wrap gap-x-2 justify-end">
      {parts.map((p, i) => (
        <span key={i} className={p.cls}>
          <span className="font-bold">{p.n}</span> {p.label}
          {i < parts.length - 1 && <span className="text-ink/25 ml-2">·</span>}
        </span>
      ))}
    </div>
  );
}

function RsvpButtons({
  session,
  onRsvp,
  compact = false,
}: {
  session: SessionDTO;
  onRsvp: (sessionId: string, intent: "yes" | "no") => void;
  compact?: boolean;
}) {
  const sizeCls = compact ? "px-4 py-2 text-sm" : "px-6 py-3 text-base";
  if (session.my_status === "confirmed" || session.my_status === "waitlist") {
    return (
      <>
        <div className={"cs-tap bg-success text-bg font-display font-bold " + sizeCls}>
          {session.my_status === "confirmed" ? "✓ You're in" : "On the waitlist"}
        </div>
        <button
          onClick={() => onRsvp(session.id, "no")}
          className={"cs-tap border-2 border-ink/15 hover:border-ink/40 font-display font-bold " + sizeCls}
        >
          Cancel RSVP
        </button>
      </>
    );
  }
  if (session.my_status === "no") {
    return (
      <>
        <button
          onClick={() => onRsvp(session.id, "yes")}
          className={"cs-tap bg-accent text-ink font-display font-bold hover:brightness-105 transition " + sizeCls}
        >
          Actually, I'm in
        </button>
        <div className={"cs-tap border-2 border-ink/15 text-ink/55 font-display font-bold " + sizeCls}>
          Marked out
        </div>
      </>
    );
  }
  return (
    <>
      <button
        onClick={() => onRsvp(session.id, "yes")}
        className={"cs-tap bg-accent text-ink font-display font-bold hover:brightness-105 transition " + sizeCls}
      >
        I'm in →
      </button>
      <button
        onClick={() => onRsvp(session.id, "no")}
        className={"cs-tap border-2 border-ink/15 hover:border-ink/40 font-display font-bold " + sizeCls}
      >
        Can't make it
      </button>
    </>
  );
}

function friendlyDelta(minutes: number): string {
  if (minutes < 60) return `in ${Math.max(1, minutes)} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 36) return `in ${hours} hr${hours === 1 ? "" : "s"}`;
  const days = Math.round(hours / 24);
  return `in ${days} day${days === 1 ? "" : "s"}`;
}

function BeaconCard({
  beacon,
  onRespond,
}: {
  beacon: BeaconDTO;
  onRespond: (beaconId: string, intent: "accept" | "decline") => void;
}) {
  const ref = useRecordView<HTMLDivElement>("beacon", beacon.id);
  const minsLeft = Math.round((new Date(beacon.expires_at).getTime() - Date.now()) / 60000);
  return (
    <div ref={ref} className="cs-reveal border-2 border-ink rounded-md p-6 bg-bg">
      <div className="flex items-center gap-2 mb-3 font-mono text-[10px] uppercase tracking-[0.18em] text-ink/55">
        <span className="px-2 py-0.5 bg-ink text-bg rounded-sm">Pickup</span>
        <span className="truncate">{beacon.group_name}</span>
      </div>
      <div className="flex items-center gap-2.5 mb-3">
        <span className="grid place-items-center w-9 h-9 rounded-full bg-ink text-bg text-xs font-bold font-mono shrink-0">
          {nameInitials(beacon.creator_name)}
        </span>
        <div className="min-w-0">
          <div className="font-display font-bold text-lg leading-tight truncate">
            {beacon.creator_name} wants a game
          </div>
          {beacon.description && (
            <div className="text-sm text-ink/60 truncate">"{beacon.description}"</div>
          )}
        </div>
      </div>
      <div className="mb-3 text-sm text-ink/70">{beacon.window_display}</div>
      <div className="mb-4 flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[10px] uppercase tracking-[0.14em] text-ink/55">
        <span>
          <b className="text-ink/80">{beacon.confirmed}/{beacon.needed}</b> in
        </span>
        {beacon.needed - beacon.confirmed > 0 && (
          <>
            <span className="text-ink/25">·</span>
            <span>
              <b>{beacon.needed - beacon.confirmed}</b> more needed
            </span>
          </>
        )}
        {minsLeft > 0 && (
          <>
            <span className="text-ink/25">·</span>
            <span className={minsLeft < 60 ? "text-warn font-bold" : ""}>
              closes {friendlyDelta(minsLeft)}
            </span>
          </>
        )}
      </div>
      {beacon.my_response === "accepted" ? (
        <div className="font-mono text-[11px] uppercase tracking-wider text-success">
          ✓ You're in
        </div>
      ) : beacon.my_response === "declined" ? (
        <div className="flex items-center gap-3">
          <span className="font-mono text-[11px] uppercase tracking-wider text-ink/45">
            You passed
          </span>
          <button
            onClick={() => onRespond(beacon.id, "accept")}
            className="cs-tap font-mono text-[11px] uppercase tracking-wider underline decoration-accent-dim decoration-2 underline-offset-2"
          >
            Changed my mind →
          </button>
        </div>
      ) : (
        <div className="flex gap-2">
          <button
            onClick={() => onRespond(beacon.id, "accept")}
            className="cs-tap px-4 py-2 bg-ink text-bg font-display font-bold text-sm"
          >
            Count me in
          </button>
          <button
            onClick={() => onRespond(beacon.id, "decline")}
            className="cs-tap px-4 py-2 border border-ink/20 font-display font-bold text-sm"
          >
            Skip
          </button>
        </div>
      )}
      <BeaconViews beacon={beacon} />
    </div>
  );
}

function MyBeaconCard({
  beacon,
  onCancel,
}: {
  beacon: BeaconDTO;
  onCancel: (beaconId: string) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const ref = useRecordView<HTMLDivElement>("beacon", beacon.id);
  const minsLeft = Math.round((new Date(beacon.expires_at).getTime() - Date.now()) / 60000);
  return (
    <div ref={ref} className="cs-reveal border-2 border-ink/30 rounded-md p-6 bg-bg">
      <div className="flex items-center gap-2 mb-3 font-mono text-[10px] uppercase tracking-[0.18em] text-ink/60">
        <span className="px-2 py-0.5 border border-ink/40 rounded-sm">Your beacon</span>
        <span className="truncate">{beacon.group_name}</span>
      </div>
      <div className="font-display font-bold text-xl leading-snug">
        "{beacon.description ?? "Anyone want to play?"}"
      </div>
      <div className="mt-2 mb-3 text-sm text-ink/70">{beacon.window_display}</div>
      <div className="mb-4 flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[10px] uppercase tracking-[0.14em] text-ink/55">
        <span>
          <b className="text-ink/80">{beacon.confirmed}/{beacon.needed}</b> in
        </span>
        <span className="text-ink/25">·</span>
        <span className={minsLeft > 0 && minsLeft < 60 ? "text-warn font-bold" : ""}>
          {minsLeft > 0 ? `closes ${friendlyDelta(minsLeft)}` : "closing"}
        </span>
      </div>
      {confirming ? (
        <div className="flex items-center gap-3">
          <span className="font-mono text-[11px] uppercase tracking-wider text-ink/55">
            Cancel this beacon?
          </span>
          <button
            onClick={() => onCancel(beacon.id)}
            className="cs-tap px-3 py-1.5 bg-warn text-bg font-display font-bold text-sm"
          >
            Yes, cancel
          </button>
          <button
            onClick={() => setConfirming(false)}
            className="cs-tap font-mono text-[11px] uppercase tracking-wider text-ink/45"
          >
            Keep it
          </button>
        </div>
      ) : (
        <button
          onClick={() => setConfirming(true)}
          className="cs-tap px-4 py-2 border border-ink/20 font-display font-bold text-sm"
        >
          Cancel beacon
        </button>
      )}
      <BeaconViews beacon={beacon} />
    </div>
  );
}
